import React, { useState, useEffect, useCallback, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import FuncionarioService from "../../services/resource/funcionarioService";
import Button from "../../componentes/button/button";
import Input from "../../componentes/input/input";
import Multiselect from "../../componentes/multiselect/multiselect";
import ComissaoService from "../../services/resource/ComissaoService";
import CargaEntregaService from "../../services/resource/escalaDeRotaService";
import DateMasked from "../../componentes/inputMask/DateMasked";
import ComissoesParciaisBloqueadas from "./ComissoesParciaisBloqueadas";
import { gerarId } from "../../utils/gerarId";
// Ajustar o caminho conforme a localização real do serviço de funcionários no projeto

const TIPOS_COMISSAO = [
    { label: "Motorista", value: "MOTORISTA" },
    { label: "Auxiliar de Entrega", value: "AUXILIAR_ENTREGA" },
];

const MESES = [
    { label: "Janeiro", value: 1 }, { label: "Fevereiro", value: 2 },
    { label: "Março", value: 3 }, { label: "Abril", value: 4 },
    { label: "Maio", value: 5 }, { label: "Junho", value: 6 },
    { label: "Julho", value: 7 }, { label: "Agosto", value: 8 },
    { label: "Setembro", value: 9 }, { label: "Outubro", value: 10 },
    { label: "Novembro", value: 11 }, { label: "Dezembro", value: 12 },
];

const hoje = new Date();

function novoPeriodo() {
    return {
        _id: gerarId(),
        inicio: null,
        fim: null,
        tipoComissao: "MOTORISTA",
    };
}

function formatarMoeda(valor) {
    if (valor === null || valor === undefined) return "-";
    return Number(valor).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatarData(data) {
    if (!data) return "";
    // "YYYY-MM-DD" vira UTC no new Date() e recua 1 dia no fuso do Brasil
    if (typeof data === "string" && /^\d{4}-\d{2}-\d{2}/.test(data)) {
        return data.substring(0, 10).split("-").reverse().join("/");
    }
    const d = data instanceof Date ? data : new Date(data);
    return d.toLocaleDateString("pt-BR");
}

function paraISO(data) {
    if (!data) return null;
    const d = data instanceof Date ? data : new Date(data);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function SectionLabel({ children }) {
    return (
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">
            {children}
        </p>
    );
}

function SkeletonLinha() {
    return (
        <div className="animate-pulse flex items-center gap-4 px-4 py-3">
            <div className="h-3 w-24 bg-muted rounded-md" />
            <div className="h-3 w-40 bg-muted rounded-md" />
            <div className="h-3 w-20 bg-muted rounded-md ml-auto" />
            <div className="h-3 w-20 bg-muted rounded-md" />
        </div>
    );
}

/**
 * Geração da comissão do distribuidor logístico (motoristas e auxiliares de entrega): geral ou parcial, com
 * substituição de função e seleção de funcionários. Na aba "Gerar Comissões" recebe a competência da página
 * (ano/mes); sozinha (rota /gerar-comissao) tem os próprios campos de ano e mês.
 */
export default function GerarComissaoDL({ ano: anoProp, mes: mesProp, onGerado } = {}) {
    const embutido = anoProp != null && mesProp != null;
    const funcionarioService = new FuncionarioService();
    const comissaoService = new ComissaoService();
    const cargaService = new CargaEntregaService();

    const [tipoComissao, setTipoComissao] = useState("MOTORISTA");
    const [anoProprio, setAno] = useState(hoje.getFullYear());
    const [mesProprio, setMes] = useState(hoje.getMonth() + 1);
    const ano = embutido ? Number(anoProp) : anoProprio;
    const mes = embutido ? Number(mesProp) : mesProprio;
    const [parcial, setParcial] = useState(false);
    const [modoMultiplosPeriodos, setModoMultiplosPeriodos] = useState(false);

    const [dataInicio, setDataInicio] = useState(null);
    const [dataFim, setDataFim] = useState(null);
    const [periodos, setPeriodos] = useState([novoPeriodo()]);

    const [funcionariosSelecionados, setFuncionariosSelecionados] = useState([]);
    const [dialogFuncionariosAberto, setDialogFuncionariosAberto] = useState(false);
    const [resultadosBusca, setResultadosBusca] = useState([]);
    const [buscandoFuncionarios, setBuscandoFuncionarios] = useState(false);
    const [funcionarios, setFuncionarios] = useState([]);

    const [filtroFiliais, setFiltroFiliais] = useState([]);
    const [filtroCargos, setFiltroCargos] = useState([]);
    const [buscaFuncionario, setBuscaFuncionario] = useState("");

    const [filiaisFiltro, setFiliaisFiltro] = useState([]);
    const [cargosFiltro, setCargosFiltro] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const [etapaAtual, setEtapaAtual] = useState("");
    const [erro, setErro] = useState(null);
    const [resultados, setResultados] = useState(null);
    // incrementa depois de cada geração para a lista de parciais bloqueadas recarregar
    const [refreshBloqueadas, setRefreshBloqueadas] = useState(0);

    // Busca de funcionários com debounce
    useEffect(() => {
        if (!dialogFuncionariosAberto || funcionarios.length > 0) return;

        const carregar = async () => {
            setBuscandoFuncionarios(true);

            try {
                const resposta = await funcionarioService.listar();
                const lista = resposta?.data ?? resposta ?? [];

                setFuncionarios(lista);
                setResultadosBusca(lista);

                // Filiais únicas
                const filiais = [
                    ...new Map(
                        lista
                            .filter(f => f.filialId || f.filial?.id)
                            .map(f => [
                                f.filialId ?? f.filial.id,
                                {
                                    id: f.filialId ?? f.filial.id,
                                    nome: f.filialNome ?? f.filial.nome
                                }
                            ])
                    ).values()
                ].sort((a, b) => a.nome.localeCompare(b.nome));

                // Cargos únicos
                const cargos = [
                    ...new Set(
                        lista
                            .map(f => f.cargo)
                            .filter(Boolean)
                    )
                ]
                    .sort()
                    .map(c => ({
                        label: c,
                        value: c
                    }));

                setFiliaisFiltro(filiais);
                setCargosFiltro(cargos);

            } finally {
                setBuscandoFuncionarios(false);
            }
        };

        carregar();
    }, [dialogFuncionariosAberto]);

    useEffect(() => {
        let lista = [...funcionarios];

        if (buscaFuncionario.trim()) {
            const termo = buscaFuncionario.toLowerCase();

            lista = lista.filter(f =>
                f.nome.toLowerCase().includes(termo)
            );
        }

        if (filtroFiliais.length > 0) {
            lista = lista.filter(f =>
                filtroFiliais.includes(f.filialId ?? f.filial?.id)
            );
        }

        if (filtroCargos.length > 0) {
            lista = lista.filter(f =>
                filtroCargos.includes(f.cargo)
            );
        }

        setResultadosBusca(lista);

    }, [
        funcionarios,
        buscaFuncionario,
        filtroFiliais,
        filtroCargos
    ]);

    const adicionarPeriodo = useCallback(() => {
        setPeriodos((atual) => [...atual, novoPeriodo()]);
    }, []);

    const removerPeriodo = useCallback((id) => {
        setPeriodos((atual) => (atual.length > 1 ? atual.filter((p) => p._id !== id) : atual));
    }, []);

    const atualizarPeriodo = useCallback((id, campo, valor) => {
        setPeriodos((atual) => atual.map((p) => (p._id === id ? { ...p, [campo]: valor } : p)));
    }, []);

    const alternarFuncionarioSelecionado = useCallback((funcionario) => {
        setFuncionariosSelecionados((atual) => {
            const jaSelecionado = atual.some((f) => f.id === funcionario.id);
            return jaSelecionado
                ? atual.filter((f) => f.id !== funcionario.id)
                : [...atual, funcionario];
        });
    }, []);

    const removerFuncionarioSelecionado = useCallback((id) => {
        setFuncionariosSelecionados((atual) => atual.filter((f) => f.id !== id));
    }, []);

    const periodosValidos = useMemo(
        () => periodos.every((p) => p.inicio && p.fim && p.fim >= p.inicio),
        [periodos]
    );

    // Cada bloco (função + período) vira uma comissão parcial própria e bloqueada
    const blocosParciais = useMemo(() => {
        if (!parcial) return [];
        return modoMultiplosPeriodos
            ? periodos.map((p) => ({ tipoComissao: p.tipoComissao, inicio: p.inicio, fim: p.fim }))
            : [{ tipoComissao, inicio: dataInicio, fim: dataFim }];
    }, [parcial, modoMultiplosPeriodos, periodos, tipoComissao, dataInicio, dataFim]);

    // blocos do mesmo pedido que se sobrepõem entre si pagariam os mesmos dias duas vezes
    const blocosSeSobrepoem = useMemo(() => {
        const validos = blocosParciais.filter((b) => b.inicio && b.fim);
        return validos.some((a, i) =>
            validos.some((b, j) => j > i && a.inicio <= b.fim && b.inicio <= a.fim));
    }, [blocosParciais]);

    const podeEnviar = useMemo(() => {
        if (!ano || !mes) return false;
        if (!parcial) return true;
        if (funcionariosSelecionados.length === 0) return false;
        if (blocosSeSobrepoem) return false;
        if (modoMultiplosPeriodos) return periodosValidos;
        return Boolean(dataInicio && dataFim && dataFim >= dataInicio);
    }, [ano, mes, parcial, funcionariosSelecionados, modoMultiplosPeriodos, periodosValidos, dataInicio, dataFim, blocosSeSobrepoem]);

    async function gerarComissoes() {
        setErro(null);
        setResultados(null);
        setEnviando(true);

        let resultadosGerados = [];

        try {
            setEtapaAtual("Organizando escalas de rotas, entregas e retornos...");
            await cargaService.vincularRotas({ ano: Number(ano), mes: Number(mes) });

            if (parcial) {
                // Uma comissão parcial por bloco (função + período), em ordem cronológica. O cálculo é do backend,
                // com as mesmas regras da geração geral; cada uma fica BLOQUEADA contra regeração geral.
                const ordenados = [...blocosParciais].sort((x, y) => x.inicio - y.inicio);

                for (const bloco of ordenados) {
                    const rotulo = TIPOS_COMISSAO.find((t) => t.value === bloco.tipoComissao)?.label;
                    setEtapaAtual(`Gerando comissão parcial · ${rotulo} · ${formatarData(bloco.inicio)} a ${formatarData(bloco.fim)}...`);

                    const resposta = await comissaoService.gerar({
                        tipoComissao: bloco.tipoComissao,
                        ano: Number(ano),
                        mes: Number(mes),
                        parcial: true,
                        dataInicio: paraISO(bloco.inicio),
                        dataFim: paraISO(bloco.fim),
                        funcionarioIds: funcionariosSelecionados.map((f) => f.id),
                    });
                    resultadosGerados = resultadosGerados.concat(resposta?.data ?? resposta ?? []);
                }
            } else {
                // Geração geral cobre motoristas e auxiliares de entrega; dias já cobertos por parciais bloqueadas
                // ficam de fora e as parciais não são tocadas.
                for (const tipo of TIPOS_COMISSAO.map((t) => t.value)) {
                    setEtapaAtual(`Gerando comissão · ${TIPOS_COMISSAO.find((t) => t.value === tipo)?.label}...`);

                    const resposta = await comissaoService.gerar({
                        tipoComissao: tipo,
                        ano: Number(ano),
                        mes: Number(mes),
                        parcial: false,
                    });
                    resultadosGerados = resultadosGerados.concat(resposta?.data ?? resposta ?? []);
                }
            }

            setResultados(resultadosGerados);
        } catch (e) {
            // o que já foi gerado antes do erro continua gravado: mostra o resultado parcial junto com a mensagem
            if (resultadosGerados.length > 0) setResultados(resultadosGerados);
            setErro(e?.response?.data?.message ?? "Não foi possível gerar as comissões. Tente novamente.");
        } finally {
            setEnviando(false);
            setEtapaAtual("");
            setRefreshBloqueadas((n) => n + 1);
            // o que foi gerado antes de um erro também está gravado: quem embute a tela atualiza a lista
            onGerado?.();
        }
    }

    return (
        <div className={embutido ? "pb-4" : "min-h-screen bg-background pb-16"}>
            {/* Topbar */}
            {!embutido && <div className="sticky top-0 z-10 bg-card border-b border-border">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-success text-success-foreground flex items-center justify-center font-bold text-sm font-display">
                        GC
                    </div>
                    <div>
                        <h1 className="text-sm font-bold font-display text-foreground">Gerar Comissões</h1>
                        <p className="text-xs text-muted-foreground">Logístico · Motoristas e Auxiliares de Entrega</p>
                    </div>
                </div>
            </div>}

            <div className={embutido ? "space-y-6" : "max-w-5xl mx-auto px-6 mt-6 space-y-6"}>

                {/* Configuração geral */}
                <div className="rounded-2xl border border-border shadow-card bg-card p-5">
                    <SectionLabel>Configuração</SectionLabel>

                    <div className={`grid grid-cols-1 gap-4 ${embutido ? "sm:grid-cols-2" : "sm:grid-cols-4"}`}>
                        <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1">Tipo de comissão</label>
                            <Dropdown
                                value={tipoComissao}
                                options={TIPOS_COMISSAO}
                                onChange={(e) => setTipoComissao(e.value)}
                                className="w-full border border-input"
                                disabled={!parcial || modoMultiplosPeriodos}
                            />
                        </div>
                        {!embutido && <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1">Ano</label>
                            <InputText
                                value={ano}
                                onChange={(e) => setAno(e.target.value.replace(/\D/g, ""))}
                                keyfilter="pint"
                                className="w-full border border-input"
                            />
                        </div>}
                        {!embutido && <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1">Mês</label>
                            <Dropdown
                                value={mes}
                                options={MESES}
                                onChange={(e) => setMes(e.value)}
                                className="w-full border border-input"
                            />
                        </div>}
                        <div className="flex items-end">
                            <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-1.5">
                                <InputSwitch
                                    checked={parcial}
                                    onChange={(e) => setParcial(e.value)}
                                />
                                Geração parcial
                            </label>
                        </div>
                    </div>

                    {!parcial && (
                        <p className="mt-4 text-xs text-muted-foreground">
                            Sem geração parcial, a comissão será calculada para todos os funcionários elegíveis
                            de <span className="font-semibold text-muted-foreground">Motorista</span> e <span className="font-semibold text-muted-foreground">Auxiliar de Entrega</span>,
                            considerando o fechamento completo do mês.
                        </p>
                    )}

                    {embutido && (
                        <p className="mt-4 text-xs text-muted-foreground">
                            Competência: <span className="font-semibold text-foreground">{String(mes).padStart(2, "0")}/{ano}</span> (altere no topo da página).
                        </p>
                    )}

                    <p className="mt-2 text-xs text-muted-foreground">
                        Antes de calcular, as escalas de rotas, entregas e retornos são sempre organizadas
                        (sincronização de rotas, entregas e retornos com as cargas do período).
                    </p>
                </div>

                {/* Configuração parcial */}
                {parcial && (
                    <div className="rounded-2xl border border-border shadow-card bg-card p-5">
                        <div className="flex items-center justify-between mb-3">
                            <SectionLabel>Período</SectionLabel>
                            <div className="flex bg-muted rounded-lg p-1 text-xs font-medium">
                                <button
                                    type="button"
                                    onClick={() => setModoMultiplosPeriodos(false)}
                                    className={`px-3 py-1 rounded-md transition-colors duration-150 ${!modoMultiplosPeriodos ? "bg-card shadow-card text-foreground" : "text-muted-foreground"
                                        }`}
                                >
                                    Período único
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setModoMultiplosPeriodos(true)}
                                    className={`px-3 py-1 rounded-md transition-colors duration-150 ${modoMultiplosPeriodos ? "bg-card shadow-card text-foreground" : "text-muted-foreground"
                                        }`}
                                >
                                    Várias funções (substituição)
                                </button>
                            </div>
                        </div>


                        {!modoMultiplosPeriodos && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-muted-foreground mb-1">Data início</label>
                                    <DateMasked
                                        value={dataInicio}
                                        onChange={(e) => setDataInicio(e)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-muted-foreground mb-1">Data fim</label>
                                    <DateMasked
                                        value={dataFim}
                                        onChange={(e) => setDataFim(e)}
                                        minDate={dataInicio}
                                    />
                                </div>
                            </div>
                        )}
                        {modoMultiplosPeriodos && (
                            <div className="space-y-3 mt-3">
                                {periodos.map((periodo, index) => (
                                    <div
                                        key={periodo._id}
                                        className="rounded-xl border border-border p-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end"
                                    >
                                        <div>
                                            <label className="block text-xs font-medium text-muted-foreground mb-1">
                                                Tipo de Comissão {index + 1}
                                            </label>
                                            <Dropdown
                                                value={periodo.tipoComissao}
                                                options={TIPOS_COMISSAO}
                                                onChange={(e) => atualizarPeriodo(periodo._id, "tipoComissao", e.value)}
                                                className="w-full border border-input"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-muted-foreground mb-1">Início</label>
                                            <DateMasked
                                                value={periodo.inicio}
                                                onChange={(e) => atualizarPeriodo(periodo._id, "inicio", e)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-muted-foreground mb-1">Fim</label>
                                            <DateMasked
                                                value={periodo.fim}
                                                onChange={(e) => atualizarPeriodo(periodo._id, "fim", e)}
                                                minDate={periodo.inicio}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removerPeriodo(periodo._id)}
                                            disabled={periodos.length === 1}
                                            className="h-9 px-3 rounded-lg text-xs font-medium text-destructive hover:bg-destructive/10 transition-colors duration-150 disabled:opacity-30 disabled:hover:bg-transparent"
                                        >
                                            Remover
                                        </button>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    onClick={adicionarPeriodo}
                                    className="text-xs font-semibold text-success hover:brightness-90 transition-all duration-150"
                                >
                                    + Adicionar período
                                </button>

                                {!periodosValidos && (
                                    <p className="text-xs text-amber-600">
                                        Verifique se todos os períodos têm início, fim, e a data fim não é anterior à data início.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Funcionários (apenas em geração parcial) */}
                {parcial && (
                    <div className="rounded-2xl border border-border shadow-card bg-card p-5">
                        <div className="flex items-center justify-between mb-3">
                            <SectionLabel>Funcionários</SectionLabel>
                            <button
                                type="button"
                                onClick={() => setDialogFuncionariosAberto(true)}
                                className="text-xs font-semibold text-success hover:brightness-90 transition-all duration-150"
                            >
                                + Selecionar funcionários
                            </button>
                        </div>

                        {funcionariosSelecionados.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                                Nenhum funcionário selecionado. A geração parcial exige ao menos um funcionário.
                            </p>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {funcionariosSelecionados.map((f) => (
                                    <span
                                        key={f.id}
                                        className="inline-flex items-center gap-2 bg-muted text-foreground text-xs font-medium rounded-full pl-3 pr-2 py-1"
                                    >
                                        {f.nome}
                                        <button
                                            type="button"
                                            onClick={() => removerFuncionarioSelecionado(f.id)}
                                            className="h-4 w-4 rounded-full bg-muted-foreground/60 hover:bg-muted-foreground/80 text-white text-[10px] leading-4 text-center transition-colors duration-150"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {parcial && (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                        <p className="font-semibold mb-1"><i className="fa fa-lock mr-1" /> Comissão parcial = bloqueada</p>
                        <p>
                            Cada função/período abaixo gera uma comissão própria, calculada com os dados reais do período
                            (cubagem, retorno, conclusão, Green Mile, telemetria e ajustes) e com as mesmas regras da geração geral.
                            Ela fica protegida: a geração geral não a recalcula nem a sobrescreve, e só paga os dias que sobrarem.
                            Não é possível gerar períodos que se sobreponham a outra parcial do funcionário.
                        </p>
                        {blocosSeSobrepoem && (
                            <p className="mt-2 font-semibold text-destructive">
                                Há períodos sobrepostos neste pedido. Ajuste as datas para continuar.
                            </p>
                        )}
                    </div>
                )}

                {/* Comissões parciais já geradas (protegidas) */}
                {!embutido && <ComissoesParciaisBloqueadas ano={ano} mes={mes} refreshKey={refreshBloqueadas} />}

                {/* Erro */}
                {erro && (
                    <div className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                        {erro}
                    </div>
                )}

                {/* Ação */}
                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={gerarComissoes}
                        disabled={!podeEnviar || enviando}
                        className="rounded-xl bg-success hover:brightness-95 disabled:bg-muted disabled:text-muted-foreground text-success-foreground text-sm font-semibold px-5 py-2.5 transition-colors duration-150 disabled:cursor-not-allowed"
                    >
                        {enviando ? "Gerando..." : "Gerar comissões"}
                    </button>
                </div>

                {/* Resultado */}
                {enviando && (
                    <div className="rounded-2xl border border-border shadow-card bg-card overflow-hidden">
                        {etapaAtual && (
                            <div className="px-4 py-3 border-b border-border flex items-center gap-2 text-xs font-medium text-muted-foreground">
                                <i className="fa fa-spinner fa-spin text-success" />
                                {etapaAtual}
                            </div>
                        )}
                        <SkeletonLinha />
                        <SkeletonLinha />
                        <SkeletonLinha />
                    </div>
                )}

                {resultados && !enviando && (
                    <div className="rounded-2xl border border-border shadow-card bg-card p-5">
                        <div className="flex items-center justify-between mb-3">
                            <SectionLabel>Resultado ({resultados.length})</SectionLabel>
                        </div>

                        <DataTable value={resultados} size="small" stripedRows emptyMessage="Nenhum resultado retornado.">
                            <Column field="funcionarioCodigo" header="Código" style={{ width: "6rem" }} />
                            <Column field="funcionarioNome" header="Funcionário" />
                            <Column field="filialNome" header="Filial" />
                            <Column
                                header="Tipo"
                                body={(r) => TIPOS_COMISSAO.find((t) => t.value === r.tipoComissao)?.label ?? "—"}
                            />
                            <Column
                                header="Período"
                                body={(r) => (
                                    <span className="whitespace-nowrap">
                                        {formatarData(r.periodoInicial)} a {formatarData(r.periodoFinal)}
                                    </span>
                                )}
                            />
                            <Column
                                header="Status"
                                body={(r) => (
                                    r.bloqueada
                                        ? <span className="whitespace-nowrap text-amber-700"><i className="fa fa-lock mr-1" />Bloqueada</span>
                                        : r.persistido === false
                                            ? <span className="text-muted-foreground">Não gravada</span>
                                            : <span className="text-muted-foreground">Geral</span>
                                )}
                            />
                            <Column
                                header="Bruto"
                                body={(r) => formatarMoeda(r.totalSemDesconto)}
                                style={{ textAlign: "right" }}
                            />
                            <Column
                                header="Descontos"
                                body={(r) => formatarMoeda(r.descontos)}
                                style={{ textAlign: "right" }}
                            />
                            <Column
                                header="Total a receber"
                                body={(r) => (
                                    <span className="font-semibold text-foreground">
                                        {formatarMoeda(r.valorTotalReceber)}
                                    </span>
                                )}
                                style={{ textAlign: "right" }}
                            />
                        </DataTable>

                        {resultados.some((r) => (r.avisos ?? []).length > 0) && (
                            <div className="mt-4 max-h-48 space-y-1 overflow-y-auto rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                                <p className="font-semibold text-foreground">Avisos</p>
                                {resultados.flatMap((r) => (r.avisos ?? []).map((aviso, i) => (
                                    <p key={`${r.funcionarioId}-${r.tipoComissao}-${i}`}>
                                        <span className="font-medium text-foreground">{r.funcionarioNome}:</span> {aviso}
                                    </p>
                                )))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Dialog de seleção de funcionários */}
            <Dialog
                header="Selecionar funcionários"
                visible={dialogFuncionariosAberto}
                onHide={() => setDialogFuncionariosAberto(false)}
                className="w-[90%]"
                blockScroll
                footer={
                    <div className="flex items-center justify-between pt-2">

                        <span className="text-sm text-muted-foreground">
                            <b>{funcionariosSelecionados.length}</b> funcionário(s) selecionado(s)
                        </span>

                        <div className="flex gap-2">

                            <Button
                                label="Limpar"
                                variant="gray"
                                iconLeft="fa fa-times"
                                onClick={() => setFuncionariosSelecionados([])}
                            />

                            <Button
                                label="Concluir"
                                variant="blue"
                                iconLeft="fa fa-check"
                                onClick={() => setDialogFuncionariosAberto(false)}
                            />

                        </div>

                    </div>

                }
            >
                <div className="space-y-4 pt-2">

                    {/* Filtros */}
                    <div className="rounded-xl border border-border bg-muted p-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <Multiselect
                                value={filtroFiliais}
                                options={filiaisFiltro}
                                onChange={(e) => setFiltroFiliais(e.value)}
                                optionLabel="nome"
                                optionValue="id"
                                placeholder="Filiais"
                                display="chip"
                                filter
                                showClear
                                className="w-full"
                            />

                            <Input
                                value={buscaFuncionario}
                                onChange={(e) => setBuscaFuncionario(e.target.value)}
                                placeholder="Buscar por nome..."
                            />

                            <Multiselect
                                value={filtroCargos}
                                options={cargosFiltro}
                                onChange={(e) => setFiltroCargos(e.value)}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Cargos"
                                display="chip"
                                filter
                                showClear
                                className="w-full"
                            />

                            <div className="flex items-end">
                                <Button
                                    label="Buscar"
                                    variant="blue"
                                    iconLeft="fa fa-check"
                                    onClick={() => setDialogFuncionariosAberto(false)}
                                />
                            </div>

                        </div>

                        <div className="flex items-center justify-between mt-4">

                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={
                                        resultadosBusca.length > 0 &&
                                        resultadosBusca.every(f =>
                                            funcionariosSelecionados.some(s => s.id === f.id)
                                        )
                                    }
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            const novos = [...funcionariosSelecionados];

                                            resultadosBusca.forEach(f => {
                                                if (!novos.some(s => s.id === f.id)) {
                                                    novos.push(f);
                                                }
                                            });

                                            setFuncionariosSelecionados(novos);
                                        } else {
                                            setFuncionariosSelecionados(prev =>
                                                prev.filter(
                                                    s => !resultadosBusca.some(f => f.id === s.id)
                                                )
                                            );
                                        }
                                    }}
                                />

                                <span className="text-sm font-medium text-foreground">
                                    Selecionar todos da lista
                                </span>
                            </label>

                            <span className="text-xs text-muted-foreground">
                                {resultadosBusca.length} encontrado(s)
                            </span>

                        </div>
                    </div>

                    {/* Lista */}
                    <div className="overflow-y-auto rounded-xl border border-border divide-y divide-border">

                        {buscandoFuncionarios ? (
                            <>
                                <SkeletonLinha />
                                <SkeletonLinha />
                                <SkeletonLinha />
                                <SkeletonLinha />
                            </>
                        ) : resultadosBusca.length === 0 ? (
                            <div className="py-10 text-center text-sm text-muted-foreground">
                                Nenhum funcionário encontrado.
                            </div>
                        ) : (
                            resultadosBusca.map((f) => {
                                const selecionado = funcionariosSelecionados.some(
                                    (s) => s.id === f.id
                                );

                                return (
                                    <button
                                        key={f.id}
                                        type="button"
                                        onClick={() => alternarFuncionarioSelecionado(f)}
                                        className={`w-full flex items-center justify-between px-4 py-3 transition-colors duration-150 ${selecionado
                                            ? "bg-success/10"
                                            : "hover:bg-muted"
                                            }`}
                                    >
                                        <div className="text-left">

                                            <div className="font-semibold text-foreground">
                                                {f.nome}
                                            </div>

                                            <div className="mt-1 text-xs text-muted-foreground flex flex-wrap gap-2">

                                                <span className="px-2 py-0.5 rounded bg-muted">
                                                    {f.cargo}
                                                </span>

                                                <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700">
                                                    {f.filialNome ?? f.filial?.nome}
                                                </span>

                                                <span className={`px-2 py-0.5 rounded ${f.ativo ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
                                                    {f.ativo === false ? "Inativo" : "Ativo"}
                                                </span>

                                            </div>

                                        </div>

                                        <div
                                            className={`h-6 w-6 rounded-full border flex items-center justify-center text-xs transition-all duration-150 ${selecionado
                                                ? "bg-success border-success text-success-foreground"
                                                : "border-input text-transparent"
                                                }`}
                                        >
                                            <i className="fa fa-check" />
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
